import Decimal from 'decimal.js';
import type { CryptoComCsvRowMeta, FiatCashEvent, FiatSubkind, LedgerEvent } from './types';
import type { FiCsvDiagnosticRow } from './fiDiagnostics';
import { taxYearUtc } from './fiTaxYear';
import {
  fiSubreasonForCryptoComIncome,
  looksLikeUnmappedRewardLikeType,
  rationaleForCryptoComIncome,
} from './fiIncomeTagging';
import { normalizeHeader, parseCsv } from './parseCsv';
import { convertGbpToEur, convertUsdToEur } from './eurFx';
import { TAX_PIPELINE } from './taxPipelineConfig';

function parseNumber(raw: string): Decimal | null {
  const t = raw.trim().replace(/\s/g, '');
  if (!t) return null;
  let x = t;
  if (x.includes(',') && x.includes('.')) {
    x = x.replace(/,/g, '');
  } else if (x.includes(',') && !x.includes('.')) {
    x = x.replace(',', '.');
  }
  try {
    const n = new Decimal(x);
    return n.isFinite() ? n : null;
  } catch {
    return null;
  }
}

function parseTs(raw: string): number | null {
  const t = raw.trim();
  if (!t) return null;
  const ms = Date.parse(t.replace(' ', 'T'));
  if (!Number.isNaN(ms)) return ms;
  const m = t.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})/);
  if (m) {
    const d = new Date(Date.UTC(Number(m[3]), Number(m[2]) - 1, Number(m[1])));
    return d.getTime();
  }
  return null;
}

function findCol(headers: string[], patterns: RegExp[]): number | undefined {
  for (let i = 0; i < headers.length; i++) {
    const h = headers[i];
    if (patterns.some((p) => p.test(h))) return i;
  }
  return undefined;
}

/** "Native Amount" but not the USD / in-USD column (normalized headers). */
function findNativeAmountNonUsd(headers: string[]): number | undefined {
  for (let i = 0; i < headers.length; i++) {
    const h = headers[i];
    if (/native/.test(h) && /amount/.test(h) && !/usd/.test(h)) return i;
  }
  return undefined;
}

function cellAt(row: string[], ix: number | undefined): string {
  if (ix === undefined) return '';
  const v = row[ix];
  return v == null ? '' : String(v).trim();
}

function buildCryptoComRowMeta(
  row: string[],
  ix: {
    ixTs: number;
    ixType: number | undefined;
    ixTransactionKind: number | undefined;
    ixTransactionDescription: number | undefined;
    ixCur: number | undefined;
    ixAmt: number | undefined;
    ixToCur: number | undefined;
    ixToAmt: number | undefined;
    ixNativeCurrency: number | undefined;
    ixNativeAmount: number | undefined;
    ixNativeAmountUsd: number | undefined;
    ixTxHash: number | undefined;
  }
): CryptoComCsvRowMeta {
  const c = (i: number | undefined) => cellAt(row, i);
  return {
    timestampUtc: c(ix.ixTs),
    transactionDescription: c(ix.ixTransactionDescription),
    currency: c(ix.ixCur),
    amount: c(ix.ixAmt),
    toCurrency: c(ix.ixToCur),
    toAmount: c(ix.ixToAmt),
    nativeCurrency: c(ix.ixNativeCurrency),
    nativeAmount: c(ix.ixNativeAmount),
    nativeAmountUsd: c(ix.ixNativeAmountUsd),
    transactionKind: c(ix.ixTransactionKind) || c(ix.ixType),
    transactionHash: c(ix.ixTxHash),
  };
}

const ACQ = new Set([
  'buy',
  'purchase',
  'crypto_purchase',
  'fiat_to_crypto',
  'crypto_viban_purchase',
  'viban_purchase',
  'deposit',
  'reward',
  'cashback',
  'rewards',
  'referral_bonus',
  'staking_reward',
  'interest',
  'crypto_earn_interest',
]);

const DISP = new Set([
  'sell',
  'crypto_to_fiat',
  'crypto_sell',
  'crypto_viban_exchange',
  'payment',
  'card_top_up',
  'fee',
  'crypto_fee',
]);

/** USD-pegged stables: compare CSV-derived EUR to 1 USD per unit × ECB daily USD→EUR (Frankfurter). */
const STABLECOIN_USD_PEG_ASSETS = new Set(['USDC', 'USDT', 'DAI', 'BUSD', 'TUSD']);

/**
 * If CSV columns imply less than 0.8 or greater than 1.2 USD per 1 coin (vs parity notionally), ignore them and use
 * daily ECB USD→EUR on the token amount (same as `convertUsdToEur(amount)` — no separate coin feed).
 */
const STABLECOIN_IMPLIED_USD_RATIO_MIN = new Decimal('0.8');
const STABLECOIN_IMPLIED_USD_RATIO_MAX = new Decimal('1.2');

/** Crypto↔crypto swaps — use with `!isInternalPlatformTransfer(type)` to avoid `exchange_to_crypto_transfer`. */
const SWAP_HINT = /crypto_to_crypto|crypto_wallet_swap|exchange|swap|convert/i;

/** VIBAN / fiat withdrawal: proceeds are usually in To Amount (EUR), not a two-crypto swap. */
function isFiatOffRampKind(typeNorm: string): boolean {
  return (
    typeNorm.includes('crypto_viban_exchange') ||
    typeNorm.includes('viban_exchange') ||
    (typeNorm.includes('viban') && typeNorm.includes('exchange') && !typeNorm.includes('purchase'))
  );
}

function tryEurFromToAmount(
  row: string[],
  ixToCur: number | undefined,
  ixToAmt: number | undefined
): Decimal | null {
  if (ixToCur === undefined || ixToAmt === undefined) return null;
  const toC = row[ixToCur]?.trim().toUpperCase().replace(/\s/g, '');
  if (toC !== 'EUR' && toC !== 'EURO') return null;
  const v = parseNumber(row[ixToAmt] ?? '');
  return v != null && !v.eq(0) ? v.abs() : null;
}

/** CDC often uses negative Native Amount for outflows (e.g. card_top_up); FMV is absolute EUR. */
function tryEurFromNativeEur(
  row: string[],
  ixNativeCurrency: number | undefined,
  ixNativeAmount: number | undefined
): Decimal | null {
  if (ixNativeCurrency === undefined || ixNativeAmount === undefined) return null;
  const nc = row[ixNativeCurrency]?.trim().toUpperCase().replace(/\s/g, '');
  if (nc !== 'EUR' && nc !== 'EURO') return null;
  const v = parseNumber(row[ixNativeAmount] ?? '');
  return v != null && !v.eq(0) ? v.abs() : null;
}

/** Native Amount (USD) / in-USD column → EUR (e.g. card_top_up with crypto Currency). */
async function tryEurFromNativeUsdColumn(
  row: string[],
  ixNativeAmountUsd: number | undefined,
  date: Date
): Promise<Decimal | null> {
  if (ixNativeAmountUsd === undefined) return null;
  const usd = parseNumber(row[ixNativeAmountUsd] ?? '');
  if (usd == null || usd.eq(0)) return null;
  return convertUsdToEur(usd.abs(), date);
}

/**
 * When **Currency** is EUR, USD, GBP, or USD-pegged stables, Amount is already in that unit
 * (Crypto.com exports); use for rows without separate EUR/USD columns.
 */
async function tryEurFromCurrencyDenomination(
  cur: string,
  amt: Decimal,
  date: Date
): Promise<Decimal | null> {
  const c = cur.trim().toUpperCase().replace(/\s/g, '');
  if (c === 'EUR' || c === 'EURO') return amt.abs().gt(0) ? amt.abs() : null;
  if (c === 'USD') return convertUsdToEur(amt.abs(), date);
  if (c === 'GBP') return convertGbpToEur(amt.abs(), date);
  if (STABLECOIN_USD_PEG_ASSETS.has(c)) {
    return convertUsdToEur(amt.abs(), date);
  }
  return null;
}

async function coerceStablecoinEurToDailyUsdParityIfOutsideBand(
  cur: string,
  amt: Decimal,
  eurTotal: Decimal,
  date: Date,
  signal?: AbortSignal
): Promise<Decimal> {
  const c = cur.trim().toUpperCase().replace(/\s/g, '');
  if (!STABLECOIN_USD_PEG_ASSETS.has(c)) return eurTotal;
  const qty = amt.abs();
  if (!qty.gt(0) || !eurTotal.gt(0)) return eurTotal;
  if (signal?.aborted) return eurTotal;

  const eurAtUsdParity = await convertUsdToEur(qty, date);
  if (signal?.aborted) return eurTotal;
  if (!eurAtUsdParity.gt(0)) return eurTotal;

  const ratio = eurTotal.div(eurAtUsdParity);
  if (ratio.gte(STABLECOIN_IMPLIED_USD_RATIO_MIN) && ratio.lte(STABLECOIN_IMPLIED_USD_RATIO_MAX)) {
    return eurTotal;
  }
  return eurAtUsdParity;
}

/** App↔exchange moves and lockups often treated as non-taxable internal (confirm with Vero). */
function isInternalPlatformTransfer(typeNorm: string): boolean {
  if (typeNorm === '') return false;
  if (typeNorm === 'exchange_to_crypto_transfer') return true;
  if (typeNorm === 'crypto_to_exchange_transfer') return true;
  if (typeNorm === 'crypto_transfer') return true;
  if (typeNorm === 'transfer_cashback') return true;
  if (typeNorm === 'lockup_unlock' || typeNorm === 'lockup_lock' || typeNorm === 'lockup_upgrade')
    return true;
  if (typeNorm.includes('finance.dpos.unstaking')) return true;
  if (typeNorm.includes('finance.dpos.staking.crypto_wallet')) return true;
  return false;
}

/** Supercharger pool → main CDC balance; same platform, not a disposal (like ending a stake). */
function isSuperchargerWithdrawalInternal(typeNorm: string): boolean {
  return typeNorm.includes('supercharger_withdrawal');
}

/**
 * Moves that were historically “conservative” disposals — under `balanced` policy they become
 * non-taxable internal (no FIFO consumption) unless `policyMode === 'conservative'`.
 */
function isConservativeMoveCategory(typeNorm: string): boolean {
  if (typeNorm.includes('withdraw')) return true;
  if (
    typeNorm.includes('exchange_->_app') ||
    typeNorm.includes('app_wallet_->_exchange')
  ) {
    return true;
  }
  if (typeNorm.includes('balance_conversion')) return true;
  if (typeNorm.includes('supercharger_lockup')) return true;
  if (typeNorm === 'cro_lockup') return true;
  if (typeNorm === 'dpos_staking_payment') return true;
  if (/^to_\+?\d|^from_\+?\d/.test(typeNorm)) return true;
  return false;
}

function conservativeInternalSubreason(typeNorm: string): string {
  if (/^from_\+?\d/.test(typeNorm)) return 'P2P_IN_FROM_PHONE';
  if (/^to_\+?\d/.test(typeNorm)) return 'P2P_OUT_TO_PHONE';
  if (typeNorm.includes('exchange_->_app') || typeNorm.includes('app_wallet_->_exchange'))
    return 'APP_EXCHANGE_BRIDGE';
  if (typeNorm.includes('balance_conversion')) return 'BALANCE_CONVERSION';
  if (typeNorm.includes('supercharger_lockup')) return 'SUPERCHARGER_LOCKUP';
  if (typeNorm === 'cro_lockup') return 'CRO_LOCKUP';
  if (typeNorm === 'dpos_staking_payment') return 'DPOS_STAKING_PAYMENT';
  if (typeNorm.includes('withdraw')) return 'WITHDRAWAL_STYLE';
  return 'OTHER_MOVE';
}

/** CDC cash CSV: pure fiat movements — not FIFO, not taxable as crypto. */
function isCdcPureFiatCashKind(typeNorm: string): boolean {
  return (
    typeNorm === 'viban_deposit' ||
    typeNorm === 'viban_withdrawal' ||
    typeNorm === 'viban_card_top_up'
  );
}

function fiatSubkindForPureCdcCash(typeNorm: string): FiatSubkind {
  if (typeNorm === 'viban_deposit') return 'DEPOSIT';
  if (typeNorm === 'viban_withdrawal') return 'WITHDRAWAL';
  if (typeNorm === 'viban_card_top_up') return 'CARD_TOP_UP';
  return 'UNKNOWN';
}

function rationaleForCdcPureFiat(sub: FiatSubkind): string {
  switch (sub) {
    case 'DEPOSIT':
      return 'FIAT / DEPOSIT / "EUR deposit via SEPA into CDC fiat wallet"';
    case 'WITHDRAWAL':
      return 'FIAT / WITHDRAWAL / "EUR withdrawal via SEPA from CDC fiat wallet"';
    case 'CARD_TOP_UP':
      return 'FIAT / CARD_TOP_UP / "Fiat card top-up from CDC cash balance"';
    default:
      return 'FIAT / CDC cash row — not a crypto acquisition or disposal.';
  }
}

/**
 * Resolve EUR value for a Crypto.com row (crypto or fiat cash) using the same column order as the main parser.
 */
async function resolveEurTotalCryptoComRow(args: {
  row: string[];
  date: Date;
  cur: string;
  amt: Decimal;
  ixEur: number | undefined;
  ixUsd: number | undefined;
  ixPrice: number | undefined;
  ixToCur: number | undefined;
  ixToAmt: number | undefined;
  ixNativeCurrency: number | undefined;
  ixNativeAmount: number | undefined;
  ixNativeAmountUsd: number | undefined;
  signal?: AbortSignal;
}): Promise<Decimal | null> {
  const {
    row,
    date,
    cur,
    amt,
    ixEur,
    ixUsd,
    ixPrice,
    ixToCur,
    ixToAmt,
    ixNativeCurrency,
    ixNativeAmount,
    ixNativeAmountUsd,
    signal,
  } = args;

  let eurTotal: Decimal | null = null;
  if (ixEur !== undefined && row[ixEur]) {
    eurTotal = parseNumber(row[ixEur]);
  }
  if (eurTotal == null && ixUsd !== undefined && row[ixUsd]) {
    const usd = parseNumber(row[ixUsd]);
    if (usd != null) {
      eurTotal = await convertUsdToEur(usd, date);
      if (signal?.aborted) return null;
    }
  }
  if (!signal?.aborted && eurTotal == null && ixPrice !== undefined && row[ixPrice]) {
    const price = parseNumber(row[ixPrice]);
    if (price != null) {
      const usdNotional = price.mul(amt.abs());
      eurTotal = await convertUsdToEur(usdNotional, date);
      if (signal?.aborted) return null;
    }
  }

  if (eurTotal == null || eurTotal.lte(0)) {
    const fromTo = tryEurFromToAmount(row, ixToCur, ixToAmt);
    if (fromTo != null) eurTotal = fromTo;
  }
  if (eurTotal == null || eurTotal.lte(0)) {
    const fromNat = tryEurFromNativeEur(row, ixNativeCurrency, ixNativeAmount);
    if (fromNat != null) eurTotal = fromNat;
  }
  if (!signal?.aborted && (eurTotal == null || eurTotal.lte(0))) {
    const fromNatUsd = await tryEurFromNativeUsdColumn(row, ixNativeAmountUsd, date);
    if (fromNatUsd != null) eurTotal = fromNatUsd;
    if (signal?.aborted) return null;
  }
  if (!signal?.aborted && (eurTotal == null || eurTotal.lte(0))) {
    const fromDenom = await tryEurFromCurrencyDenomination(cur, amt, date);
    if (fromDenom != null) eurTotal = fromDenom;
    if (signal?.aborted) return null;
  }

  if (eurTotal != null && eurTotal.gt(0) && !signal?.aborted) {
    eurTotal = await coerceStablecoinEurToDailyUsdParityIfOutsideBand(cur, amt, eurTotal, date, signal);
  }

  return eurTotal;
}

function platformInternalSubreason(typeNorm: string): string {
  if (typeNorm === 'exchange_to_crypto_transfer') return 'EXCHANGE_TO_APP';
  if (typeNorm === 'crypto_to_exchange_transfer') return 'APP_TO_EXCHANGE';
  if (typeNorm === 'crypto_transfer') return 'CRYPTO_TRANSFER';
  if (typeNorm === 'transfer_cashback') return 'TRANSFER_CASHBACK';
  if (typeNorm === 'lockup_unlock') return 'LOCKUP_UNLOCK';
  if (typeNorm === 'lockup_lock') return 'LOCKUP_LOCK';
  if (typeNorm === 'lockup_upgrade') return 'LOCKUP_UPGRADE';
  if (typeNorm.includes('finance.dpos.unstaking')) return 'DPOS_UNSTAKE';
  if (typeNorm.includes('finance.dpos.staking')) return 'DPOS_STAKE';
  return 'PLATFORM_INTERNAL';
}

function pushNonTaxableInternal(
  events: LedgerEvent[],
  diagnostics: FiCsvDiagnosticRow[],
  args: {
    ts: number;
    csvRow: number;
    ref: string;
    typeNorm: string;
    cur: string;
    absAmt: Decimal;
    csvMeta: CryptoComCsvRowMeta;
    subreason: string;
    note: string;
    diagnosticCategory?: 'NON_TAXABLE_INTERNAL' | 'AMBIGUOUS_REVIEW';
  }
): void {
  const {
    ts,
    csvRow,
    ref,
    typeNorm,
    cur,
    absAmt,
    csvMeta,
    subreason,
    note,
    diagnosticCategory = 'NON_TAXABLE_INTERNAL',
  } = args;
  events.push({
    ts,
    source: 'crypto_com',
    kind: 'internal_transfer',
    asset: cur,
    amount: absAmt,
    eurValue: null,
    ref,
    csvMeta,
    classification: 'INTERNAL_TRANSFER',
    fiTaxCategory: 'NON_TAXABLE_INTERNAL',
    fiSubreason: subreason,
    explanation: note,
    reviewFlags:
      diagnosticCategory === 'AMBIGUOUS_REVIEW'
        ? ['non_taxable_internal', 'fifo_excluded', 'ambiguous_review']
        : ['non_taxable_internal', 'fifo_excluded'],
  });
  diagnostics.push({
    source: 'crypto_com',
    csv_row: csvRow,
    ref,
    transaction_kind: typeNorm,
    asset: cur,
    category: diagnosticCategory,
    subreason,
    note,
  });
}

/** Card / earn rewards at FMV when received (income → acquisition lot in FIFO). */
function isRewardIncomeType(typeNorm: string): boolean {
  /** Mission / task rewards (Crypto.com app). */
  if (typeNorm === 'rewards_platform_deposit_credited') return true;
  /** Visa card cashback; `referral_card_cashback` = referral/promo card cashback, paid in CRO (same tax treatment). */
  if (typeNorm === 'card_cashback' || typeNorm === 'referral_card_cashback') return true;
  if (typeNorm === 'mco_stake_reward') return true;
  if (typeNorm === 'reimbursement') return true;
  if (typeNorm === 'admin_wallet_credited') return true;
  if (typeNorm === 'referral_gift') return true;
  if (typeNorm.includes('non_compound_interest')) return true;
  if (
    typeNorm.includes('mystery_box') ||
    typeNorm.includes('cro_pool_reward') ||
    typeNorm.includes('supercharger_reward') ||
    typeNorm.includes('trading_rebate') ||
    typeNorm.includes('cro_lockup_reward') ||
    typeNorm.includes('card_rebate:') ||
    typeNorm.includes('competition_reward') ||
    typeNorm.includes('campaign_reward') ||
    typeNorm.includes('nft_payout') ||
    typeNorm.includes('referral_bonus_reward') ||
    typeNorm.includes('referral_bonus') ||
    typeNorm.includes('pay_rewards') ||
    typeNorm.includes('pay_reward') ||
    typeNorm.includes('sign-up_bonus') ||
    typeNorm.includes('adjustment_(credit)') ||
    typeNorm === 'cro_unlock' ||
    typeNorm.includes('cro_unlock')
  ) {
    return true;
  }
  if (typeNorm.includes('dpos_')) return true;
  return false;
}

function pushCryptoComTaxableIncome(
  events: LedgerEvent[],
  diagnostics: FiCsvDiagnosticRow[],
  args: {
    ts: number;
    cur: string;
    absAmt: Decimal;
    absEur: Decimal;
    ref: string;
    csvMeta: CryptoComCsvRowMeta;
    typeNorm: string;
    csvRow: number;
  }
): void {
  const { ts, cur, absAmt, absEur, ref, csvMeta, typeNorm, csvRow } = args;
  const reviewFlags: string[] = [];
  let explanation = rationaleForCryptoComIncome(typeNorm);

  if (absEur.lte(0)) {
    reviewFlags.push('income_valuation_gap');
    explanation +=
      ' EUR FMV at receipt is zero or missing after import rules — verify native EUR/USD columns in Crypto.com export; FIFO may show 0 acquisition basis.';
    diagnostics.push({
      source: 'crypto_com',
      csv_row: csvRow,
      ref,
      transaction_kind: typeNorm,
      asset: cur,
      category: 'INCOME_PIPELINE',
      subreason: 'INCOME_VALUATION_GAP',
      note: `No positive EUR FMV resolved for this income row (FMV=${absEur.toFixed(8)} EUR). Manual valuation may be required for filing.`,
    });
  } else if (absEur.lt(0.01)) {
    reviewFlags.push('income_dust_fmv');
    explanation +=
      ' EUR FMV under €0.01 (dust); full precision is in the income CSV (EUR_FMV_at_receipt_full_eur). Confirm against app history if needed.';
    diagnostics.push({
      source: 'crypto_com',
      csv_row: csvRow,
      ref,
      transaction_kind: typeNorm,
      asset: cur,
      category: 'INCOME_PIPELINE',
      subreason: 'INCOME_DUST_OR_ROUNDING',
      note: `FMV ${absEur.toFixed(8)} EUR — if the export showed €0.00, use the full-precision column; Supercharger rewards are often small per row.`,
    });
  }

  if (typeNorm.includes('supercharger_reward') && absEur.gt(0) && absEur.lt(0.05)) {
    diagnostics.push({
      source: 'crypto_com',
      csv_row: csvRow,
      ref,
      transaction_kind: typeNorm,
      asset: cur,
      category: 'INCOME_PIPELINE',
      subreason: 'SUPERCHARGER_FMV_REVIEW',
      note: `Small reward FMV (${absEur.toFixed(8)} EUR). Audited in income export with full precision; not silently rounded away.`,
    });
  }

  events.push({
    ts,
    source: 'crypto_com',
    kind: 'income',
    asset: cur,
    amount: absAmt,
    eurValue: absEur,
    ref,
    csvMeta,
    classification: 'INCOME',
    fiTaxCategory: 'TAXABLE_INCOME',
    fiSubreason: fiSubreasonForCryptoComIncome(typeNorm),
    explanation,
    reviewFlags: reviewFlags.length ? reviewFlags : undefined,
  });
}

export type ParseCryptoComCsvOptions = {
  /** Called for major steps and periodically while scanning rows (can be slow due to FX API). */
  onProgress?: (message: string) => void;
  /** `await` briefly every N rows so the UI can repaint (default 20). */
  yieldEvery?: number;
  /** When aborted, returns events/skips compiled so far (no throw). */
  signal?: AbortSignal;
};

/** Map Crypto.com (and similar) CSV exports to ledger events. */
export async function parseCryptoComCsv(
  text: string,
  options?: ParseCryptoComCsvOptions
): Promise<{
  events: LedgerEvent[];
  skipped: string[];
  aborted: boolean;
  diagnostics: FiCsvDiagnosticRow[];
  fiatCashEvents: FiatCashEvent[];
}> {
  const onProgress = options?.onProgress;
  const yieldEvery = options?.yieldEvery ?? 20;
  const signal = options?.signal;

  const rows = parseCsv(text.trim());
  if (rows.length < 2) {
    return {
      events: [],
      skipped: ['CSV has no data rows'],
      aborted: false,
      diagnostics: [],
      fiatCashEvents: [],
    };
  }

  const dataRows = rows.length - 1;
  onProgress?.(`CSV: ${dataRows} data rows detected; parsing…`);

  const rawHeaders = rows[0].map((c) => normalizeHeader(c));
  const skipped: string[] = [];
  const events: LedgerEvent[] = [];
  const fiatCashEvents: FiatCashEvent[] = [];
  const diagnostics: FiCsvDiagnosticRow[] = [];
  let conservativeMoveRows = 0;
  let emptyTypeSkips = 0;

  const ixTs =
    findCol(rawHeaders, [/utc_time/, /timestamp/, /^date$/i, /^time/]) ?? 0;
  const ixTransactionKind = findCol(rawHeaders, [/transaction_kind/, /transaction_type/, /^type$/]);
  const ixTransactionDescription = findCol(rawHeaders, [/transaction_description/]);
  const ixType =
    ixTransactionKind ??
    ixTransactionDescription ??
    findCol(rawHeaders, [/transaction_kind/, /transaction_type/, /^type$/, /description/]);
  const ixCur = findCol(rawHeaders, [/^currency$/, /^coin$/, /^token$/]);
  const ixAmt = findCol(rawHeaders, [/^amount$/, /^quantity$/]);
  const ixToCur = findCol(rawHeaders, [/to_currency/, /currency_to/]);
  const ixToAmt = findCol(rawHeaders, [/to_amount/, /amount_to/]);

  const ixNativeCurrency = findCol(rawHeaders, [/native_currency/, /^native_currency$/]);
  const ixNativeAmountUsd = findCol(rawHeaders, [/native_amount.*usd/, /in_usd/, /native_amount_in_usd/]);
  const ixNativeAmount = findNativeAmountNonUsd(rawHeaders);
  const ixTxHash = findCol(rawHeaders, [/transaction_hash/, /tx_hash/, /txid/]);

  /** Do not use bare `/eur/` — it matches the `Currency` column header. */
  const ixEur =
    findCol(rawHeaders, [/^eur$/i, /euro/i, /fiat.*eur/, /native_amount_eur/]) ??
    findCol(rawHeaders, [/total.*eur/i]);

  const ixUsd =
    findCol(rawHeaders, [/usd/, /dollar/, /native_amount_usd/, /total.*usd/i]) ??
    findCol(rawHeaders, [/^total$/i]);

  const ixPrice = findCol(rawHeaders, [/price$/i, /^rate$/]);

  if (ixType === undefined || ixCur === undefined || ixAmt === undefined) {
    skipped.push(
      'Could not find required columns (need transaction type, currency, amount).'
    );
  }

  /**
   * Crypto.com often emits **two** rows for one VIBAN crypto→fiat sale (`crypto_viban` and
   * `crypto_viban_exchange`, same ts / asset / |amount|). Both map to disposals — skip duplicates.
   */
  const vibanCryptoSaleDedupKeys = new Set<string>();

  for (let r = 1; r < rows.length; r++) {
    if (signal?.aborted) {
      skipped.push(
        `Parse stopped by user after row ${r} of ${dataRows} (data rows processed up to here are kept; this row onward was not applied).`
      );
      break;
    }

    if (r === 1 || r % 25 === 0 || r === rows.length - 1) {
      onProgress?.(`Rows ${r}/${dataRows} (FX lookups may take a while)…`);
    }
    if (yieldEvery > 0 && r % yieldEvery === 0) {
      await new Promise((res) => setTimeout(res, 0));
      if (signal?.aborted) {
        skipped.push(
          `Parse stopped by user after row ${r} of ${dataRows} (data rows processed up to here are kept; this row onward was not applied).`
        );
        break;
      }
    }

    const row = rows[r];
    const tsRaw = row[ixTs] ?? '';
    const ts = parseTs(tsRaw);
    if (ts == null) {
      skipped.push(`Row ${r + 1}: bad date "${tsRaw}"`);
      continue;
    }
    const date = new Date(ts);

    const typeRaw = (ixType !== undefined ? row[ixType] : '').trim().toLowerCase();
    const typeNorm = typeRaw.replace(/\s+/g, '_');

    if (typeRaw === '') {
      emptyTypeSkips++;
      continue;
    }

    const cur = (ixCur !== undefined ? row[ixCur] : '').trim().toUpperCase();
    const amt = parseNumber(ixAmt !== undefined ? row[ixAmt] : '');

    const csvMeta = buildCryptoComRowMeta(row, {
      ixTs,
      ixType,
      ixTransactionKind,
      ixTransactionDescription,
      ixCur,
      ixAmt,
      ixToCur,
      ixToAmt,
      ixNativeCurrency,
      ixNativeAmount,
      ixNativeAmountUsd,
      ixTxHash,
    });

    if (isCdcPureFiatCashKind(typeNorm)) {
      if (!cur || amt == null || amt.eq(0)) {
        skipped.push(`Row ${r + 1}: fiat cash row needs currency and non-zero amount`);
        continue;
      }
      const eurFiat = await resolveEurTotalCryptoComRow({
        row,
        date,
        cur,
        amt,
        ixEur,
        ixUsd,
        ixPrice,
        ixToCur,
        ixToAmt,
        ixNativeCurrency,
        ixNativeAmount,
        ixNativeAmountUsd,
        signal,
      });
      if (signal?.aborted) {
        skipped.push(
          `Parse stopped during FX for row ${r + 1}; that row was not applied. Earlier rows are kept.`
        );
        break;
      }
      const sub = fiatSubkindForPureCdcCash(typeNorm);
      const hashRef = csvMeta.transactionHash?.trim();
      fiatCashEvents.push({
        kind: 'fiat',
        fiatSubkind: sub,
        ts,
        taxYear: taxYearUtc(ts),
        currency: csvMeta.currency,
        amount: csvMeta.amount,
        toCurrency: csvMeta.toCurrency,
        toAmount: csvMeta.toAmount,
        nativeCurrency: csvMeta.nativeCurrency,
        nativeAmount: csvMeta.nativeAmount,
        nativeAmountUsd: csvMeta.nativeAmountUsd,
        sourcePlatform: 'crypto_com',
        sourceType: csvMeta.transactionKind || typeNorm,
        transactionDescription: csvMeta.transactionDescription,
        reference: hashRef || `crypto_com_fiat_${r + 1}`,
        rationale: rationaleForCdcPureFiat(sub),
        eurReconciliation:
          eurFiat != null && eurFiat.gt(0) ? eurFiat.abs() : null,
      });
      continue;
    }

    if (!cur || amt == null || amt.eq(0)) {
      skipped.push(`Row ${r + 1}: missing currency or amount`);
      continue;
    }

    if (isSuperchargerWithdrawalInternal(typeNorm)) {
      pushNonTaxableInternal(events, diagnostics, {
        ts,
        csvRow: r + 1,
        ref: `crypto_com_internal_${r + 1}`,
        typeNorm,
        cur,
        absAmt: amt.abs(),
        csvMeta,
        subreason: 'SUPERCHARGER_WITHDRAWAL',
        note: 'Supercharger pool ↔ app balance; beneficial ownership unchanged (balanced policy: non-taxable internal).',
      });
      continue;
    }

    if (isInternalPlatformTransfer(typeNorm)) {
      pushNonTaxableInternal(events, diagnostics, {
        ts,
        csvRow: r + 1,
        ref: `crypto_com_internal_${r + 1}`,
        typeNorm,
        cur,
        absAmt: amt.abs(),
        csvMeta,
        subreason: platformInternalSubreason(typeNorm),
        note: 'App/exchange/lockup/DPOS stake move; non-taxable internal (balanced policy).',
      });
      continue;
    }

    let eurTotal = await resolveEurTotalCryptoComRow({
      row,
      date,
      cur,
      amt,
      ixEur,
      ixUsd,
      ixPrice,
      ixToCur,
      ixToAmt,
      ixNativeCurrency,
      ixNativeAmount,
      ixNativeAmountUsd,
      signal,
    });
    if (signal?.aborted) {
      skipped.push(
        `Parse stopped during FX for row ${r + 1}; that row was not applied. Earlier rows are kept.`
      );
      break;
    }

    const ref = `crypto_com_row_${r + 1}`;

    /** `crypto_viban` = sell crypto for fiat (single disposal). `viban_purchase` = buy crypto with fiat (single acquisition). Do not treat as two-leg swap. */
    const isSwap =
      typeNorm !== 'crypto_viban' &&
      typeNorm !== 'viban_purchase' &&
      !isInternalPlatformTransfer(typeNorm) &&
      !isFiatOffRampKind(typeNorm) &&
      (SWAP_HINT.test(typeNorm) ||
        (ixToCur !== undefined &&
          ixToAmt !== undefined &&
          row[ixToCur] &&
          row[ixToAmt]));

    if (eurTotal == null || eurTotal.lte(0)) {
      if (isRewardIncomeType(typeNorm)) {
        const absEur =
          eurTotal != null && eurTotal.gt(0) ? eurTotal.abs() : new Decimal(0);
        pushCryptoComTaxableIncome(events, diagnostics, {
          ts,
          cur,
          absAmt: amt.abs(),
          absEur,
          ref,
          csvMeta,
          typeNorm,
          csvRow: r + 1,
        });
        continue;
      }
    }

    if (isSwap && ixToCur !== undefined && ixToAmt !== undefined) {
      const toCur = row[ixToCur]?.trim().toUpperCase();
      const toAmt = parseNumber(row[ixToAmt] ?? '');
      if (toCur && toAmt && !toAmt.eq(0) && eurTotal && eurTotal.gt(0)) {
        events.push({
          ts,
          source: 'crypto_com',
          kind: 'disposal',
          asset: cur,
          amount: amt.abs(),
          eurValue: eurTotal,
          ref,
          csvMeta,
        });
        events.push({
          ts,
          source: 'crypto_com',
          kind: 'acquisition',
          asset: toCur,
          amount: toAmt.abs(),
          eurValue: eurTotal,
          ref: `${ref}_to`,
          csvMeta,
        });
        continue;
      }
    }

    if (eurTotal == null || eurTotal.lte(0)) {
      skipped.push(
        `Row ${r + 1} (${typeNorm} ${cur}): need positive EUR proceeds (EUR/USD columns, or To Amount when To Currency is EUR, or Native Amount when Native Currency is EUR).`
      );
      continue;
    }

    const absAmt = amt.abs();
    const absEur = eurTotal.abs();

    if (typeNorm === 'crypto_viban' || typeNorm === 'crypto_viban_exchange') {
      const vk = `${ts}|${cur.toUpperCase()}|${absAmt.toFixed(12)}`;
      if (vibanCryptoSaleDedupKeys.has(vk)) {
        continue;
      }
      vibanCryptoSaleDedupKeys.add(vk);
    }

    if (typeNorm === 'crypto_viban') {
      events.push({
        ts,
        source: 'crypto_com',
        kind: 'disposal',
        asset: cur,
        amount: absAmt,
        eurValue: absEur,
        ref,
        csvMeta,
        classification: 'TRANSFER_OUT',
        fiTaxCategory: 'TAXABLE_DISPOSAL',
        explanation:
          'Crypto sold via CDC VIBAN (crypto_viban); fiat proceeds from To Amount / Native EUR — taxable disposal for FIFO.',
      });
      continue;
    }

    if (typeNorm === 'viban_purchase') {
      events.push({
        ts,
        source: 'crypto_com',
        kind: 'acquisition',
        asset: cur,
        amount: absAmt,
        eurValue: absEur,
        ref,
        csvMeta,
        classification: 'TRANSFER_IN',
        fiTaxCategory: 'ACQUISITION',
        explanation:
          'Fiat-to-crypto purchase via CDC VIBAN (viban_purchase); EUR basis from native/to columns — FIFO acquisition lot.',
      });
      continue;
    }

    if (isConservativeMoveCategory(typeNorm)) {
      if (TAX_PIPELINE.fi.policyMode === 'conservative') {
        const isIncomingP2P = /^from_\+?\d/.test(typeNorm);
        events.push({
          ts,
          source: 'crypto_com',
          kind: isIncomingP2P ? 'acquisition' : 'disposal',
          asset: cur,
          amount: absAmt,
          eurValue: absEur,
          ref: `crypto_com_conservative_${r + 1}`,
          csvMeta,
        });
        conservativeMoveRows++;
      } else {
        pushNonTaxableInternal(events, diagnostics, {
          ts,
          csvRow: r + 1,
          ref: `crypto_com_internal_${r + 1}`,
          typeNorm,
          cur,
          absAmt,
          csvMeta,
          subreason: conservativeInternalSubreason(typeNorm),
          note:
            'Former “conservative move” row: treated as non-taxable internal under balanced policy (basis preserved in inventory; no FIFO disposal). Confirm P2P/withdrawals with Vero if needed.',
        });
      }
      continue;
    }

    if (typeNorm.startsWith('sold_')) {
      events.push({
        ts,
        source: 'crypto_com',
        kind: 'disposal',
        asset: cur,
        amount: absAmt,
        eurValue: absEur,
        ref,
        csvMeta,
      });
      continue;
    }

    if (typeNorm.startsWith('bought_')) {
      events.push({
        ts,
        source: 'crypto_com',
        kind: 'acquisition',
        asset: cur,
        amount: absAmt,
        eurValue: absEur,
        ref,
        csvMeta,
      });
      continue;
    }

    if (typeNorm === 'card_cashback_reversal' || typeNorm === 'card_cashback_reverted') {
      events.push({
        ts,
        source: 'crypto_com',
        kind: 'disposal',
        asset: cur,
        amount: absAmt,
        eurValue: absEur,
        ref,
        csvMeta,
      });
      continue;
    }

    if (typeNorm.includes('crypto_wallet_swap_debited')) {
      events.push({
        ts,
        source: 'crypto_com',
        kind: 'disposal',
        asset: cur,
        amount: absAmt,
        eurValue: absEur,
        ref,
        csvMeta,
      });
      continue;
    }
    if (typeNorm.includes('crypto_wallet_swap_credited')) {
      events.push({
        ts,
        source: 'crypto_com',
        kind: 'acquisition',
        asset: cur,
        amount: absAmt,
        eurValue: absEur,
        ref,
        csvMeta,
      });
      continue;
    }

    if (
      looksLikeUnmappedRewardLikeType(typeNorm) &&
      !isRewardIncomeType(typeNorm) &&
      !ACQ.has(typeNorm) &&
      !isInternalPlatformTransfer(typeNorm)
    ) {
      events.push({
        ts,
        source: 'crypto_com',
        kind: 'acquisition',
        asset: cur,
        amount: absAmt,
        eurValue: absEur,
        ref,
        csvMeta,
        classification: 'TRANSFER_IN',
        fiTaxCategory: 'ACQUISITION',
        reviewFlags: ['ambiguous_income_candidate'],
        explanation:
          'Label looks reward-like but is not mapped to taxable income — booked as acquisition at FMV; see income diagnostics and review.',
      });
      diagnostics.push({
        source: 'crypto_com',
        csv_row: r + 1,
        ref,
        transaction_kind: typeNorm,
        asset: cur,
        category: 'AMBIGUOUS_REVIEW',
        subreason: 'UNMAPPED_REWARD_LIKE_TYPE',
        note: 'Confirm whether this should be taxed as income at receipt; adjust mapping in cryptoComCsv.ts if needed.',
      });
      continue;
    }

    if (isRewardIncomeType(typeNorm)) {
      pushCryptoComTaxableIncome(events, diagnostics, {
        ts,
        cur,
        absAmt,
        absEur: absEur,
        ref,
        csvMeta,
        typeNorm,
        csvRow: r + 1,
      });
      continue;
    }

    if (ACQ.has(typeNorm) || typeNorm.includes('purchase') || typeNorm.includes('deposit')) {
      const asIncome =
        typeNorm.includes('reward') ||
        typeNorm.includes('cashback') ||
        typeNorm === 'staking_reward' ||
        typeNorm === 'interest' ||
        typeNorm === 'crypto_earn_interest';
      if (asIncome) {
        pushCryptoComTaxableIncome(events, diagnostics, {
          ts,
          cur,
          absAmt,
          absEur: absEur,
          ref,
          csvMeta,
          typeNorm,
          csvRow: r + 1,
        });
      } else {
        events.push({
          ts,
          source: 'crypto_com',
          kind: 'acquisition',
          asset: cur,
          amount: absAmt,
          eurValue: absEur,
          ref,
          csvMeta,
          classification: 'TRANSFER_IN',
          fiTaxCategory: 'ACQUISITION',
          explanation: 'Purchase or deposit at FMV — cost basis for FIFO.',
        });
      }
      continue;
    }

    if (DISP.has(typeNorm) || typeNorm.includes('sell') || typeNorm.includes('fiat')) {
      events.push({
        ts,
        source: 'crypto_com',
        kind: 'disposal',
        asset: cur,
        amount: absAmt,
        eurValue: absEur,
        ref,
        csvMeta,
      });
      continue;
    }

    if (typeNorm.includes('withdraw') || typeNorm.includes('transfer')) {
      if (TAX_PIPELINE.fi.policyMode === 'balanced') {
        pushNonTaxableInternal(events, diagnostics, {
          ts,
          csvRow: r + 1,
          ref: `crypto_com_internal_${r + 1}`,
          typeNorm,
          cur,
          absAmt,
          csvMeta,
          subreason: 'AMBIGUOUS_TRANSFER',
          diagnosticCategory: 'AMBIGUOUS_REVIEW',
          note:
            'Unclassified withdraw/transfer string: excluded from taxable disposal by default under balanced policy — review if this is an external gift/sale.',
        });
      } else {
        skipped.push(`Row ${r + 1}: "${typeNorm}" not classified (may be non-taxable transfer).`);
      }
      continue;
    }

    skipped.push(`Row ${r + 1}: unclassified type "${typeNorm}" — map it in cryptoComCsv.ts or fix CSV.`);
  }

  if (emptyTypeSkips > 0) {
    skipped.push(
      `Skipped ${emptyTypeSkips} row(s) with empty transaction type (often CSV footer/summary lines).`
    );
  }

  if (conservativeMoveRows > 0) {
    skipped.push(
      `Conservative policy: ${conservativeMoveRows} move row(s) counted as disposal/acquisition at FMV (set policyMode conservative in taxPipelineConfig to keep this).`
    );
  }

  if (TAX_PIPELINE.fi.policyMode === 'balanced' && diagnostics.length > 0) {
    skipped.push(
      `Balanced policy: ${diagnostics.length} non-taxable internal / review row(s) recorded — see diagnostics CSV and rows with kind internal_transfer in full ledger.`
    );
  }

  const aborted = signal?.aborted === true;
  onProgress?.(
    aborted
      ? `Stopped: ${events.length} ledger events merged so far, ${skipped.length} CSV notes/skips.`
      : `Finished: ${events.length} ledger events, ${skipped.length} CSV notes/skips.`
  );

  return {
    events: dedupeCryptoComVibanDisposals(events),
    skipped,
    aborted,
    diagnostics,
    fiatCashEvents,
  };
}

/** Same ts + asset + |amount| as crypto_viban / crypto_viban_exchange pair (CDC double row for one sale). */
function vibanDisposalGroupKey(e: LedgerEvent): string | null {
  if (e.source !== 'crypto_com' || e.kind !== 'disposal') return null;
  const tk = (e.csvMeta?.transactionKind ?? '').trim().toLowerCase().replace(/\s+/g, '_');
  if (tk !== 'crypto_viban' && tk !== 'crypto_viban_exchange') return null;
  const absAmt = e.amount.abs().toFixed(12);
  return `${e.ts}|${e.asset.trim().toUpperCase()}|${absAmt}`;
}

/** Prefer `crypto_viban` over `crypto_viban_exchange` (primary leg); tie-break by ref. */
function vibanSaleKindPriority(csvMeta: CryptoComCsvRowMeta | undefined): number {
  const tk = (csvMeta?.transactionKind ?? '').trim().toLowerCase().replace(/\s+/g, '_');
  if (tk === 'crypto_viban') return 0;
  if (tk === 'crypto_viban_exchange') return 1;
  return 2;
}

/**
 * CDC exports two rows for one VIBAN crypto→fiat sale (`crypto_viban` + `crypto_viban_exchange`).
 * In-parse dedup usually keeps one, but `mergeEvents` keys include `ref`, so re-imports or multi-part
 * CSVs can leave **two** disposals → second FIFO pass matches **no** inventory (0 cost, full proceeds).
 * Collapse to one disposal per (ts, asset, |amount|).
 */
export function dedupeCryptoComVibanDisposals(events: LedgerEvent[]): LedgerEvent[] {
  const byKey = new Map<string, LedgerEvent[]>();
  const rest: LedgerEvent[] = [];

  for (const e of events) {
    const k = vibanDisposalGroupKey(e);
    if (k == null) {
      rest.push(e);
      continue;
    }
    const list = byKey.get(k) ?? [];
    list.push(e);
    byKey.set(k, list);
  }

  const out: LedgerEvent[] = [...rest];
  byKey.forEach((list) => {
    if (list.length === 1) {
      out.push(list[0]!);
      return;
    }
    list.sort((a, b) => {
      const pa = vibanSaleKindPriority(a.csvMeta);
      const pb = vibanSaleKindPriority(b.csvMeta);
      if (pa !== pb) return pa - pb;
      return String(a.ref ?? '').localeCompare(String(b.ref ?? ''));
    });
    out.push(list[0]!);
  });
  return out.sort((a, b) => a.ts - b.ts);
}
